import { Flex, Skeleton, Switch, Typography, notification } from 'antd';

import type { BallooningSettings } from '../../../../interfaces/Ballooning';
import {
	useGetBallooningSettingsQuery,
	useUpdateBallooningSettingsMutation,
} from '../../../../redux/api/ballooning';
import { extractApiError } from '../../../../utils/apiError';

const { Paragraph, Text, Title } = Typography;

type DefaultKey = Exclude<keyof BallooningSettings, 'modified_time'>;

const DEFAULTS: { key: DefaultKey; label: string; description: string }[] = [
	{
		key: 'default_balloon_gdt',
		label: 'Balloon GD&T',
		description:
			'Feature control frames (position, flatness, profile, …) and datum features.',
	},
	{
		key: 'default_auto_keyence',
		label: 'Choose Keyence vs hand gage automatically',
		description:
			'Dimensions the Keyence can see in silhouette use the device mapped to the part’s production method below. Off, every dimension is Hand Gage.',
	},
];

/** What AI inspection plans do unless the user picks otherwise in Generate Inspection Plan. */
export default function GenerationDefaultsSection() {
	const {
		data: settings,
		isLoading,
		isError,
	} = useGetBallooningSettingsQuery();
	const [updateSettings, { isLoading: isSaving, originalArgs }] =
		useUpdateBallooningSettingsMutation();

	const handleChange = async (key: DefaultKey, value: boolean) => {
		try {
			await updateSettings({ [key]: value }).unwrap();
		} catch (err) {
			notification.error({
				message: 'Could not save the default',
				description: extractApiError(err),
			});
		}
	};

	return (
		<section aria-labelledby="ai-plan-defaults-title">
			<Title level={5} id="ai-plan-defaults-title" style={{ marginTop: 0 }}>
				AI inspection plan defaults
			</Title>
			<Paragraph type="secondary">
				Pre-selected in Generate Inspection Plan, and used as is when a drawing
				arriving from the ERP is ballooned automatically.
			</Paragraph>
			{isLoading ? (
				<Skeleton active paragraph={{ rows: 2 }} title={false} />
			) : isError || !settings ? (
				<Text type="danger">The ballooning settings could not be loaded.</Text>
			) : (
				<Flex vertical gap={12}>
					{DEFAULTS.map(({ key, label, description }) => (
						<Flex key={key} gap={12} align="flex-start">
							<Switch
								checked={settings[key]}
								loading={isSaving && originalArgs?.[key] !== undefined}
								onChange={(value) => handleChange(key, value)}
								aria-labelledby={`${key}-label`}
								aria-describedby={`${key}-description`}
								data-testid={`ballooning-setting-${key}`}
							/>
							<div>
								<Text strong id={`${key}-label`}>
									{label}
								</Text>
								<br />
								<Text type="secondary" id={`${key}-description`}>
									{description}
								</Text>
							</div>
						</Flex>
					))}
				</Flex>
			)}
		</section>
	);
}
